import { useState, useRef, useEffect, useCallback } from "react";
import {
  ACCOUNTS, BUDGET_CATEGORIES, SPENDING_DATA,
  NET_WORTH_DATA, INVESTMENTS, RECURRING_TRANSACTIONS,
} from "../../data/mockData";
import { useIsMobile } from "../../hooks/useIsMobile";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

// ─── Build the financial context payload sent to the backend ─────────────────
function buildFinancialContext() {
  const nwFirst       = NET_WORTH_DATA[0].value;
  const nwLast        = NET_WORTH_DATA[NET_WORTH_DATA.length - 1].value;
  const nwPrev        = NET_WORTH_DATA[NET_WORTH_DATA.length - 2].value;
  const netWorthGrowth6m = ((nwLast - nwFirst) / nwFirst) * 100;
  const monthlyChange    = nwLast - nwPrev;

  const recent        = SPENDING_DATA[SPENDING_DATA.length - 1];
  const savedThisMonth = recent.income - recent.spending;
  const savingsRate    = (savedThisMonth / recent.income) * 100;

  const debtAccts  = ACCOUNTS.filter(a => a.balance < 0);
  const totalDebt  = Math.abs(debtAccts.reduce((s, a) => s + a.balance, 0));

  const totalBudget  = BUDGET_CATEGORIES.reduce((s, c) => s + c.allocated, 0);
  const totalSpent   = BUDGET_CATEGORIES.reduce((s, c) => s + c.spent, 0);

  const totalInvest  = INVESTMENTS.reduce((s, i) => s + i.value, 0);
  const totalGain    = INVESTMENTS.reduce((s, i) => s + i.gainAmt, 0);
  const avgGain      = INVESTMENTS.reduce((s, i) => s + i.gain, 0) / INVESTMENTS.length;

  const recurringTotal = RECURRING_TRANSACTIONS
    .filter(r => r.active)
    .reduce((s, r) => s + r.amount, 0);

  return {
    netWorth:        nwLast,
    netWorthGrowth6m,
    monthlyChange,
    accounts:        ACCOUNTS.map(a => ({ name: a.name, bank: a.bank, balance: a.balance, change: a.change })),
    budget: {
      categories:  BUDGET_CATEGORIES.map(c => ({ name: c.name, allocated: c.allocated, spent: c.spent })),
      totalBudget,
      totalSpent,
    },
    savingsRate,
    savedThisMonth,
    monthlyIncome:   recent.income,
    monthlySpending: recent.spending,
    investments: {
      holdings:   INVESTMENTS.map(i => ({ ticker: i.ticker, name: i.name, value: i.value, gain: i.gain, gainAmt: i.gainAmt })),
      totalValue: totalInvest,
      totalGain,
      avgGain,
    },
    debt: {
      accounts: debtAccts.map(a => ({ name: a.name, balance: a.balance, change: a.change })),
      total:    totalDebt,
    },
    spendingTrend:   SPENDING_DATA.slice(-6),
    recurringTotal,
  };
}

// ─── Suggested prompts ────────────────────────────────────────────────────────
const SUGGESTIONS = [
  { icon: "◈", text: "Give me a complete financial summary" },
  { icon: "◎", text: "How is my budget tracking this month?" },
  { icon: "△", text: "Analyse my investment portfolio" },
  { icon: "◉", text: "How can I improve my savings rate?" },
  { icon: "⊞", text: "What's the fastest way to pay down my debt?" },
  { icon: "✦", text: "Any financial tips for my situation?" },
];

// ─── Single message bubble ────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div style={{
      display: "flex",
      flexDirection: isUser ? "row-reverse" : "row",
      alignItems: "flex-end",
      gap: "8px",
      marginBottom: "14px",
    }}>
      {/* Avatar */}
      {!isUser && (
        <div style={{
          width: "28px", height: "28px", borderRadius: "8px", flexShrink: 0,
          background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "12px", color: "#0F1A10", fontWeight: "700",
        }}>✦</div>
      )}
      {/* Bubble */}
      <div style={{
        maxWidth: "82%",
        background: isUser ? "rgba(212,175,55,0.12)" : "var(--bg-elevated)",
        border: isUser ? "1px solid rgba(212,175,55,0.3)" : "1px solid var(--border-strong)",
        borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        padding: "10px 14px",
      }}>
        <div style={{
          fontSize: "13px",
          color: "var(--text-primary)",
          fontFamily: "'Capsule Sans', sans-serif",
          lineHeight: 1.65,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {msg.content}
        </div>
        <div style={{
          fontSize: "9px", marginTop: "5px", textAlign: isUser ? "right" : "left",
          color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace",
        }}>
          {msg.time}
        </div>
      </div>
    </div>
  );
}

// ─── Typing indicator ─────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", marginBottom: "14px" }}>
      <div style={{
        width: "28px", height: "28px", borderRadius: "8px", flexShrink: 0,
        background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "12px", color: "#0F1A10", fontWeight: "700",
      }}>✦</div>
      <div style={{
        background: "var(--bg-elevated)", border: "1px solid var(--border-strong)",
        borderRadius: "14px 14px 14px 4px", padding: "12px 16px",
        display: "flex", gap: "4px", alignItems: "center",
      }}>
        <style>{`
          @keyframes coach-dot { 0%,60%,100% { opacity:0.2; transform:translateY(0); }
            30% { opacity:1; transform:translateY(-3px); } }
        `}</style>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: "5px", height: "5px", borderRadius: "50%", background: "#D4AF37",
            animation: `coach-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CoachChat({ token }) {
  const isMobile    = useIsMobile();
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [unread, setUnread]   = useState(0);
  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const contextRef  = useRef(null);

  // Build context once and cache
  if (!contextRef.current) contextRef.current = buildFinancialContext();

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const now = () => new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setInput("");
    setError(null);

    const userMsg = { role: "user", content: trimmed, time: now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // History for the API: only role + content, no UI fields
    const history = messages.map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch(`${API}/api/v1/coach/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          message:           trimmed,
          financialContext:  contextRef.current,
          history,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Coach unavailable");

      const aiMsg = { role: "assistant", content: data.reply, time: now() };
      setMessages(prev => [...prev, aiMsg]);
      if (!open) setUnread(u => u + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, token, open]);

  // Send on Enter (Shift+Enter = new line)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const panelW = isMobile ? "100vw" : "400px";
  const panelH = isMobile ? "100dvh" : "580px";

  return (
    <>
      {/* ── Floating launch button ──────────────────────────────────────── */}
      <button
        onClick={() => setOpen(o => !o)}
        title="AI Financial Coach"
        style={{
          position: "fixed",
          bottom: isMobile ? "74px" : "28px",
          right: isMobile ? "16px" : "28px",
          zIndex: 500,
          width: isMobile ? "50px" : "auto",
          height: isMobile ? "50px" : "auto",
          padding: isMobile ? "0" : "11px 20px",
          borderRadius: isMobile ? "50%" : "24px",
          background: open ? "var(--bg-elevated)" : "linear-gradient(135deg, #D4AF37, #9A7C1E)",
          border: open ? "1px solid var(--border-strong)" : "none",
          color: open ? "var(--text-secondary)" : "#0F1A10",
          fontSize: isMobile ? "20px" : "13px",
          fontFamily: "'Capsule Sans', sans-serif",
          fontWeight: "700",
          cursor: "pointer",
          boxShadow: open ? "none" : "0 4px 20px rgba(212,175,55,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
          transition: "all 0.2s",
          letterSpacing: "-0.01em",
        }}
      >
        {open ? (
          <span style={{ fontSize: "18px" }}>×</span>
        ) : (
          <>
            <span style={{ fontSize: isMobile ? "20px" : "14px" }}>✦</span>
            {!isMobile && <span>AI Coach</span>}
          </>
        )}
        {/* Unread badge */}
        {!open && unread > 0 && (
          <div style={{
            position: "absolute", top: "-4px", right: "-4px",
            width: "18px", height: "18px", borderRadius: "50%",
            background: "#F87171", color: "#fff",
            fontSize: "10px", fontWeight: "700",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{unread}</div>
        )}
      </button>

      {/* ── Chat panel ──────────────────────────────────────────────────── */}
      {open && (
        <div style={{
          position: "fixed",
          bottom: isMobile ? "0" : "88px",
          right: isMobile ? "0" : "28px",
          width: panelW,
          height: panelH,
          zIndex: 499,
          background: "var(--bg-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: isMobile ? "20px 20px 0 0" : "20px",
          boxShadow: "0 16px 60px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "coachIn 0.22s cubic-bezier(0.4,0,0.2,1)",
        }}>
          <style>{`
            @keyframes coachIn {
              from { opacity:0; transform: translateY(16px) scale(0.98); }
              to   { opacity:1; transform: translateY(0) scale(1); }
            }
          `}</style>

          {/* Header */}
          <div style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--border)",
            display: "flex", alignItems: "center", gap: "12px",
            background: "var(--bg-elevated)",
            flexShrink: 0,
          }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "10px",
              background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "16px", color: "#0F1A10", fontWeight: "700", flexShrink: 0,
            }}>✦</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "var(--text-primary)", lineHeight: 1 }}>
                AI Financial Coach
              </div>
              <div style={{ fontSize: "10px", fontFamily: "'JetBrains Mono', monospace", color: "#52D68A", marginTop: "3px" }}>
                ● Analysing your financial data
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: "20px", cursor: "pointer", padding: "2px 6px", lineHeight: 1 }}
            >×</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: "auto", padding: "20px 16px 8px",
            display: "flex", flexDirection: "column",
          }}>
            {/* Empty state with suggestions */}
            {messages.length === 0 && !loading && (
              <div>
                <div style={{ textAlign: "center", marginBottom: "24px" }}>
                  <div style={{ fontSize: "28px", marginBottom: "8px" }}>✦</div>
                  <div style={{ fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)", marginBottom: "4px" }}>
                    Your personal finance analyst
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                    Ask me anything about your finances
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s.text)}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "10px 14px", borderRadius: "10px",
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border)",
                        color: "var(--text-secondary)",
                        fontSize: "12px", fontFamily: "'Capsule Sans', sans-serif",
                        cursor: "pointer", display: "flex", alignItems: "center", gap: "10px",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(212,175,55,0.4)"; e.currentTarget.style.color = "var(--text-primary)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
                    >
                      <span style={{ color: "#D4AF37", fontSize: "13px", flexShrink: 0 }}>{s.icon}</span>
                      {s.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message list */}
            {messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)}
            {loading && <TypingDots />}

            {/* Error */}
            {error && (
              <div style={{
                margin: "8px 0", padding: "10px 14px", borderRadius: "10px",
                background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)",
                fontSize: "12px", color: "#F87171", fontFamily: "'JetBrains Mono', monospace",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
              }}>
                <span>⚠ {error}</span>
                <button onClick={() => setError(null)} style={{ background: "none", border: "none", color: "#F87171", cursor: "pointer", fontSize: "14px" }}>×</button>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input bar */}
          <div style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--border)",
            display: "flex", gap: "10px", alignItems: "flex-end",
            background: "var(--bg-elevated)",
            flexShrink: 0,
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your finances…"
              rows={1}
              style={{
                flex: 1,
                padding: "10px 14px",
                borderRadius: "12px",
                background: "var(--bg-main)",
                border: "1px solid var(--border-strong)",
                color: "var(--text-primary)",
                fontSize: "13px",
                fontFamily: "'Capsule Sans', sans-serif",
                resize: "none",
                outline: "none",
                lineHeight: 1.5,
                maxHeight: "100px",
                overflowY: "auto",
                transition: "border-color 0.15s",
              }}
              onFocus={e => { e.target.style.borderColor = "rgba(212,175,55,0.5)"; }}
              onBlur={e => { e.target.style.borderColor = "var(--border-strong)"; }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{
                width: "38px", height: "38px", borderRadius: "10px",
                background: (!input.trim() || loading) ? "var(--bg-hover)" : "linear-gradient(135deg, #D4AF37, #9A7C1E)",
                border: "none",
                color: (!input.trim() || loading) ? "var(--text-faint)" : "#0F1A10",
                fontSize: "16px", cursor: (!input.trim() || loading) ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s", flexShrink: 0,
              }}
            >
              {loading ? (
                <div style={{
                  width: "14px", height: "14px", borderRadius: "50%",
                  border: "2px solid var(--text-faint)", borderTopColor: "transparent",
                  animation: "spin 0.8s linear infinite",
                }} />
              ) : "↑"}
            </button>
          </div>

          {/* Rate limit note */}
          <div style={{ padding: "6px 16px 10px", textAlign: "center", background: "var(--bg-elevated)" }}>
            <span style={{ fontSize: "9px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
              30 messages/hour · Powered by Claude
            </span>
          </div>
        </div>
      )}
    </>
  );
}
